// Logistics — vuelos, hoteles y edición (Fase B Nivel 2).
import { Link } from "react-router-dom";
import { ChevronLeft, Plane, Bed, Clock, Hash, AlertTriangle, ExternalLink, Edit3, Plus, Car, Calendar } from "lucide-react";
import { useMemo, useState } from "react";
import { TRIP as STATIC_TRIP } from "../data/trip";
import { formatDate, formatTime } from "../data/helpers";
import { useTripBase } from "../hooks/useTripBase";
import { useTrip } from "../auth/TripContext";
import EditSheet, { Field, inputCls } from "../components/EditSheet";
import FlightActions from "../components/FlightActions";
import DocumentsSection from "../components/DocumentsSection";

function uid(prefix) { return `${prefix}-${Math.random().toString(36).slice(2, 9)}`; }

// Compact card for a companion (private) flight nested inside the parent tramo.
function CompanionFlightCard({ comp, parentDepart, canEdit, onEdit }) {
  const sameFlight = parentDepart && comp.depart && parentDepart.slice(0, 13) === comp.depart.slice(0, 13);
  return (
    <div
      className="mt-4 -mx-1 p-3 rounded-xl border-2 border-purple-200 bg-purple-50/60 relative"
      data-testid={`companion-flight-${comp.id}`}
    >
      <div className="flex items-center justify-between mb-2 pr-7">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-1.5 py-0.5 rounded-full bg-purple-600 text-white text-[9px] font-bold uppercase tracking-wider">
            🤫 Sorpresa · {comp.passengers?.split(" ")[0] || "Privado"}
          </span>
          {sameFlight ? (
            <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wider">
              Mismo vuelo
            </span>
          ) : (
            <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold uppercase tracking-wider">
              Vuelo separado
            </span>
          )}
        </div>
        {canEdit && (
          <button
            onClick={() => onEdit(comp)}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white border border-purple-200 flex items-center justify-center active:scale-95"
            aria-label="Editar"
            data-testid={`edit-flight-${comp.id}`}
          >
            <Edit3 size={11} />
          </button>
        )}
      </div>
      <p className="text-sm font-bold leading-tight text-purple-950">{comp.airline} · {comp.code}</p>
      <p className="text-xs text-purple-900/70 mt-0.5">
        {comp.from?.code} {formatTime(comp.depart)} → {comp.to?.code} {formatTime(comp.arrive)} · {comp.duration}
      </p>
      <div className="mt-2 space-y-1 text-[11px] text-purple-950">
        {comp.bookingRef && <p><span className="text-purple-700/70">Reserva:</span> <span className="font-mono font-semibold">{comp.bookingRef}</span></p>}
        {comp.seats && <p><span className="text-purple-700/70">Asiento:</span> <span className="font-mono font-semibold">{comp.seats}</span></p>}
        {comp.price && <p><span className="text-purple-700/70">Costo:</span> {comp.price}</p>}
        {comp.notes && <p className="mt-1.5 leading-relaxed text-purple-900/90">📌 {comp.notes}</p>}
      </div>
      <p className="mt-2 pt-2 border-t border-purple-200 text-[10px] text-purple-700/70 italic">
        🔒 Solo tú ves esto. Oculto en el link compartido.
      </p>
    </div>
  );
}

function emptyFlight() {
  return {
    id: uid("f"),
    airline: "",
    code: "",
    from: { code: "", city: "", terminal: "" },
    to: { code: "", city: "", terminal: "" },
    depart: "",
    arrive: "",
    duration: "",
    bookingRef: "",
    seats: "",
    bags: "",
    notes: "",
    pending: false,
  };
}

function emptyHotel() {
  return {
    id: uid("h"),
    city: "",
    name: "",
    address: "",
    nights: 1,
    checkIn: "",
    checkOut: "",
    bookingRef: "",
    pin: "",
    phone: "",
    breakfast: false,
    neighborhood: "",
    tip: "",
    lat: 0,
    lng: 0,
  };
}

export default function Logistics() {
  const base = useTripBase();
  const { trip, tripId, saveTripData } = useTrip();
  // Use the current trip when loaded; show empty state while null (no legacy leak).
  const T = trip || {};
  const allFlights = T.flights || [];
  // Top-level: own flights only. Companion (private) flights nest inside their `linkedTo` parent.
  const flights = allFlights.filter((f) => !f.private);
  const companionsByParent = useMemo(() => {
    const m = {};
    for (const f of allFlights) {
      if (f.private && f.linkedTo) {
        (m[f.linkedTo] = m[f.linkedTo] || []).push(f);
      }
    }
    return m;
  }, [allFlights]);
  // Orphan companion flights (private with no linkedTo, or pointing to an unknown parent) — render at end.
  const orphanCompanions = allFlights.filter(
    (f) => f.private && (!f.linkedTo || !allFlights.some((x) => x.id === f.linkedTo))
  );
  const hotels = T.hotels || [];
  const cars = T.cars || [];
  const canEdit = !!tripId;

  const [editingFlight, setEditingFlight] = useState(null);
  const [editingHotel, setEditingHotel] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNewFlight = () => setEditingFlight({ ...emptyFlight(), _new: true });
  const openNewHotel = () => setEditingHotel({ ...emptyHotel(), _new: true });
  const openEditFlight = (f) => setEditingFlight(JSON.parse(JSON.stringify(f)));
  const openEditHotel = (h) => setEditingHotel(JSON.parse(JSON.stringify(h)));

  const saveFlight = async () => {
    if (!editingFlight) return;
    setSaving(true);
    const { _new, ...clean } = editingFlight;
    const next = JSON.parse(JSON.stringify(T));
    next.flights = next.flights || [];
    if (_new) next.flights.push(clean);
    else next.flights = next.flights.map((f) => (f.id === clean.id ? clean : f));
    try { await saveTripData(next); } finally { setSaving(false); setEditingFlight(null); }
  };

  const deleteFlight = async () => {
    if (!editingFlight) return;
    if (!window.confirm("¿Eliminar este vuelo?")) return;
    setSaving(true);
    const next = JSON.parse(JSON.stringify(T));
    next.flights = (next.flights || []).filter((f) => f.id !== editingFlight.id);
    try { await saveTripData(next); } finally { setSaving(false); setEditingFlight(null); }
  };

  const saveHotel = async () => {
    if (!editingHotel) return;
    setSaving(true);
    const { _new, ...clean } = editingHotel;
    clean.nights = Number(clean.nights) || 1;
    clean.lat = Number(clean.lat) || 0;
    clean.lng = Number(clean.lng) || 0;
    const next = JSON.parse(JSON.stringify(T));
    next.hotels = next.hotels || [];
    if (_new) next.hotels.push(clean);
    else next.hotels = next.hotels.map((h) => (h.id === clean.id ? clean : h));
    try { await saveTripData(next); } finally { setSaving(false); setEditingHotel(null); }
  };

  const deleteHotel = async () => {
    if (!editingHotel) return;
    if (!window.confirm("¿Eliminar este hotel?")) return;
    setSaving(true);
    const next = JSON.parse(JSON.stringify(T));
    next.hotels = (next.hotels || []).filter((h) => h.id !== editingHotel.id);
    try { await saveTripData(next); } finally { setSaving(false); setEditingHotel(null); }
  };

  // Field updaters
  const f = editingFlight;
  const setF = (patch) => setEditingFlight((x) => ({ ...x, ...patch }));
  const setFFrom = (patch) => setEditingFlight((x) => ({ ...x, from: { ...x.from, ...patch } }));
  const setFTo = (patch) => setEditingFlight((x) => ({ ...x, to: { ...x.to, ...patch } }));
  const h = editingHotel;
  const setH = (patch) => setEditingHotel((x) => ({ ...x, ...patch }));

  return (
    <div className="container-app pt-6 pb-28" data-testid="logistics-page">
      <Link to={base || "/"} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted))] mb-4" data-testid="logistics-back">
        <ChevronLeft size={16} /> Inicio
      </Link>
      <h1 className="text-3xl font-bold leading-tight">Logística del viaje</h1>
      <p className="text-sm text-[hsl(var(--muted))] mt-1 mb-6">Vuelos, autos, hoteles y documentos en un solo lugar</p>

      {/* Vuelos */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="section-title flex items-center gap-2">
            <Plane size={14} /> Vuelos ({flights.length})
            {(Object.keys(companionsByParent).length + orphanCompanions.length) > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[9px] font-bold uppercase tracking-wider" title="Vuelos privados visibles solo para ti">
                + {allFlights.filter((f) => f.private).length} 🤫
              </span>
            )}
          </h3>
          {canEdit && (
            <button onClick={openNewFlight} className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--accent))] active:scale-95" data-testid="add-flight-btn">
              <Plus size={14} /> Añadir
            </button>
          )}
        </div>
        <ul className="space-y-3">
          {flights.map((flight, i) => (
            <li key={flight.id} className="card-paper p-4 relative" data-testid={`flight-${flight.id}`}>
              {canEdit && (
                <button onClick={() => openEditFlight(flight)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[hsl(var(--bg))] border border-[hsl(var(--line))] flex items-center justify-center active:scale-95" aria-label="Editar" data-testid={`edit-flight-${flight.id}`}>
                  <Edit3 size={13} />
                </button>
              )}
              <div className="flex items-start justify-between gap-3 mb-3 pr-10">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">
                    Tramo {i + 1}
                  </p>
                  <p className="font-bold text-lg leading-tight">{flight.airline || "Sin aerolínea"}</p>
                  <p className="text-sm text-[hsl(var(--ink-soft))]">{flight.code}</p>
                </div>
                {flight.pending && <span className="chip-warn">Por confirmar</span>}
                {flight.warning && <span className="chip-warn"><AlertTriangle size={11} /> Revisar</span>}
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold leading-none">{flight.from?.code || "—"}</p>
                  <p className="text-xs text-[hsl(var(--muted))] mt-1">{flight.from?.city}</p>
                  {flight.from?.terminal && <p className="text-[11px] text-[hsl(var(--muted))]">{flight.from.terminal}</p>}
                  {flight.depart && <p className="text-sm font-medium mt-2">{formatTime(flight.depart)}</p>}
                  {flight.depart && <p className="text-[11px] text-[hsl(var(--muted))]">{formatDate(flight.depart)}</p>}
                </div>
                <div className="flex-1 px-3 text-center">
                  <div className="h-px bg-[hsl(var(--line))] relative">
                    <Plane size={16} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[hsl(var(--accent))] bg-[hsl(var(--bg-card))] px-0.5" style={{ width: 24 }} />
                  </div>
                  <p className="text-[10px] text-[hsl(var(--muted))] mt-1">{flight.duration}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold leading-none">{flight.to?.code || "—"}</p>
                  <p className="text-xs text-[hsl(var(--muted))] mt-1">{flight.to?.city}</p>
                  {flight.to?.terminal && <p className="text-[11px] text-[hsl(var(--muted))]">{flight.to.terminal}</p>}
                  {flight.arrive && <p className="text-sm font-medium mt-2">{formatTime(flight.arrive)}</p>}
                  {flight.arrive && <p className="text-[11px] text-[hsl(var(--muted))]">{formatDate(flight.arrive)}</p>}
                </div>
              </div>
              {(flight.bookingRef || flight.seats || flight.bags || flight.notes) && (
                <div className="mt-4 pt-3 border-t border-[hsl(var(--line))] space-y-1.5 text-xs">
                  {flight.bookingRef && <p className="flex gap-2 items-center"><Hash size={12} className="text-[hsl(var(--muted))]" /><span className="text-[hsl(var(--muted))]">Reserva:</span><span className="font-mono font-semibold">{flight.bookingRef}</span></p>}
                  {flight.seats && <p><span className="text-[hsl(var(--muted))]">Asientos:</span> <span className="font-mono font-semibold">{flight.seats}</span></p>}
                  {flight.bags && <p><span className="text-[hsl(var(--muted))]">Equipaje:</span> {flight.bags}</p>}
                  {flight.notes && <p className="text-[hsl(var(--ink-soft))] mt-2 leading-relaxed">📌 {flight.notes}</p>}
                </div>
              )}

              {/* Acciones: check-in + boarding pass */}
              <FlightActions flight={flight} tripId={tripId} />

              {/* Companion (private) flights linked to this tramo — only visible to admin */}
              {(companionsByParent[flight.id] || []).map((comp) => (
                <CompanionFlightCard
                  key={comp.id}
                  comp={comp}
                  parentDepart={flight.depart}
                  canEdit={canEdit}
                  onEdit={openEditFlight}
                />
              ))}
            </li>
          ))}
          {/* Orphan companion flights (no linkedTo or unknown parent) — display standalone */}
          {orphanCompanions.map((comp) => (
            <li key={comp.id} className="card-paper p-4 relative border-2 border-purple-200 bg-purple-50/40" data-testid={`flight-${comp.id}`}>
              {canEdit && (
                <button onClick={() => openEditFlight(comp)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white border border-purple-200 flex items-center justify-center active:scale-95" aria-label="Editar" data-testid={`edit-flight-${comp.id}`}>
                  <Edit3 size={13} />
                </button>
              )}
              <div className="flex items-start gap-2 mb-3 pr-10">
                <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[9px] font-bold uppercase tracking-wider" title="No visible en el link compartido">
                  🤫 {comp.passengers?.split(" ")[0] || "Privado"}
                </span>
                <div>
                  <p className="font-bold text-base leading-tight">{comp.airline}</p>
                  <p className="text-xs text-[hsl(var(--ink-soft))]">{comp.code}</p>
                </div>
              </div>
              <p className="text-xs text-[hsl(var(--muted))]">{comp.from?.code} → {comp.to?.code} · {formatDate(comp.depart)} {formatTime(comp.depart)}</p>
              {comp.notes && <p className="text-[11px] text-purple-900/80 mt-2 leading-relaxed">📌 {comp.notes}</p>}
            </li>
          ))}
        </ul>
      </section>

      {/* Documentos de viaje — visas, ETAs, pasaportes */}
      <DocumentsSection documents={T.documents || []} />

      {/* Autos de alquiler */}
      {cars.length > 0 && (
        <section className="mb-8" data-testid="cars-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="section-title flex items-center gap-2">
              <Car size={14} /> Autos de alquiler ({cars.length})
            </h3>
          </div>
          <ul className="space-y-3">
            {cars.map((c) => (
              <li key={c.id} className="card-paper p-4" data-testid={`car-${c.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-white border border-[hsl(var(--line))] flex items-center justify-center shrink-0 overflow-hidden">
                    {c.logoUrl ? (
                      <img src={c.logoUrl} alt={c.company} className="w-full h-full object-contain p-1.5" />
                    ) : (
                      <Car size={22} className="text-[hsl(var(--accent))]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">{c.company || "Auto de alquiler"}</p>
                    <p className="font-bold text-base leading-tight">{c.model}</p>
                    {c.specs && (
                      <p className="text-xs text-[hsl(var(--muted))] mt-0.5">
                        {[c.specs.transmission, c.specs.seats && `${c.specs.seats} pax`, c.specs.bags, c.specs.ac && "A/C"].filter(Boolean).join(" · ")}
                      </p>
                    )}
                  </div>
                </div>

                {c.price && (
                  <div className="-mx-4 px-4 py-2 mb-3 bg-[hsl(var(--accent))]/8 border-y border-[hsl(var(--line))]">
                    <p className="text-sm font-bold text-[hsl(var(--accent))]">{c.price}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-3 pt-1">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold mb-1 flex items-center gap-1">
                      <Calendar size={10} /> Recogida
                    </p>
                    <p className="text-sm font-semibold">{formatDate(c.pickup?.at)} · {formatTime(c.pickup?.at)}</p>
                    <p className="text-xs text-[hsl(var(--muted))] mt-0.5">{c.pickup?.location}</p>
                    {c.pickup?.instructions && <p className="text-[11px] text-[hsl(var(--ink-soft))] mt-1 leading-relaxed">📍 {c.pickup.instructions}</p>}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold mb-1 flex items-center gap-1">
                      <Calendar size={10} /> Devolución
                    </p>
                    <p className="text-sm font-semibold">{formatDate(c.dropoff?.at)} · {formatTime(c.dropoff?.at)}</p>
                    <p className="text-xs text-[hsl(var(--muted))] mt-0.5">{c.dropoff?.location}</p>
                  </div>
                </div>

                {(c.bookingRef || c.driverPhone || c.phone) && (
                  <div className="mt-3 pt-3 border-t border-[hsl(var(--line))] space-y-1.5 text-xs">
                    {c.bookingRef && <p><span className="text-[hsl(var(--muted))]">Reserva:</span> <span className="font-mono font-semibold">{c.bookingRef}</span></p>}
                    {c.driver && <p><span className="text-[hsl(var(--muted))]">Conductor:</span> <span className="font-semibold">{c.driver}</span></p>}
                    {c.phone && <p><span className="text-[hsl(var(--muted))]">Tel:</span> <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="font-mono">{c.phone}</a></p>}
                    {c.url && <p><a href={c.url} target="_blank" rel="noreferrer" className="text-[hsl(var(--accent))] inline-flex items-center gap-1"><ExternalLink size={11} /> Gestionar reserva</a></p>}
                    {c.tip && <p className="text-[hsl(var(--ink-soft))] mt-2 leading-relaxed">💡 {c.tip}</p>}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Hoteles */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="section-title flex items-center gap-2">
            <Bed size={14} /> Hoteles ({hotels.length})
          </h3>
          {canEdit && (
            <button onClick={openNewHotel} className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(var(--accent))] active:scale-95" data-testid="add-hotel-btn">
              <Plus size={14} /> Añadir
            </button>
          )}
        </div>
        <ul className="space-y-3">
          {hotels.map((hotel) => (
            <li key={hotel.id} className="card-paper p-4 relative" data-testid={`hotel-${hotel.id}`}>
              {canEdit && (
                <button onClick={() => openEditHotel(hotel)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-[hsl(var(--bg))] border border-[hsl(var(--line))] flex items-center justify-center active:scale-95" aria-label="Editar" data-testid={`edit-hotel-${hotel.id}`}>
                  <Edit3 size={13} />
                </button>
              )}
              <div className="flex items-start justify-between gap-3 mb-2 pr-10">
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">{hotel.city} · {hotel.nights} noches</p>
                  <p className="font-bold text-base leading-tight mt-1">{hotel.name}</p>
                  <p className="text-xs text-[hsl(var(--muted))] mt-1">{hotel.address}</p>
                </div>
                {hotel.breakfast && <span className="chip-gold">🥐 Desayuno</span>}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-[hsl(var(--line))]">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">Check-in</p>
                  <p className="text-sm font-semibold">{formatDate(hotel.checkIn)}</p>
                  <p className="text-xs text-[hsl(var(--muted))]">{formatTime(hotel.checkIn)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">Check-out</p>
                  <p className="text-sm font-semibold">{formatDate(hotel.checkOut)}</p>
                  <p className="text-xs text-[hsl(var(--muted))]">{formatTime(hotel.checkOut)}</p>
                </div>
              </div>
              {(hotel.bookingRef || hotel.pin || hotel.phone) && (
                <div className="mt-3 pt-3 border-t border-[hsl(var(--line))] space-y-1.5 text-xs">
                  {hotel.bookingRef && <p><span className="text-[hsl(var(--muted))]">Reserva:</span> <span className="font-mono font-semibold">{hotel.bookingRef}</span></p>}
                  {hotel.pin && <p><span className="text-[hsl(var(--muted))]">PIN:</span> <span className="font-mono font-semibold">{hotel.pin}</span></p>}
                  {hotel.phone && <p><span className="text-[hsl(var(--muted))]">Tel:</span> <a href={`tel:${hotel.phone.replace(/\s/g, "")}`} className="font-mono">{hotel.phone}</a></p>}
                  {hotel.neighborhood && <p className="text-[hsl(var(--ink-soft))] mt-2">📍 {hotel.neighborhood}</p>}
                  {hotel.tip && <p className="text-[hsl(var(--ink-soft))] mt-1">💡 {hotel.tip}</p>}
                </div>
              )}
              {hotel.walkTo && hotel.walkTo.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[hsl(var(--line))]">
                  <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold mb-2">A pie desde el hotel</p>
                  <ul className="grid grid-cols-2 gap-1.5">
                    {hotel.walkTo.map((w, i) => (
                      <li key={i} className="text-xs flex items-center gap-1.5">
                        <Clock size={11} className="text-[hsl(var(--muted))]" />
                        <span className="truncate">{w.name}</span>
                        <span className="text-[hsl(var(--accent))] font-semibold shrink-0">{w.min}'</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {hotel.lat && hotel.lng && (
                <a href={`https://www.google.com/maps/search/?api=1&query=${hotel.lat},${hotel.lng}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--accent))]" data-testid={`hotel-map-${hotel.id}`}>
                  Abrir en mapas <ExternalLink size={11} />
                </a>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* EDIT FLIGHT SHEET */}
      <EditSheet
        open={!!f}
        title={f?._new ? "Añadir vuelo" : "Editar vuelo"}
        onClose={() => setEditingFlight(null)}
        onSave={saveFlight}
        onDelete={f?._new ? null : deleteFlight}
        saving={saving}
      >
        {f && (
          <>
            <Field label="Aerolínea"><input value={f.airline} onChange={(e) => setF({ airline: e.target.value })} placeholder="Iberia" className={inputCls} data-testid="ef-airline" /></Field>
            <Field label="N° vuelo"><input value={f.code} onChange={(e) => setF({ code: e.target.value })} placeholder="IB6830" className={inputCls} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Origen (código)"><input value={f.from.code} onChange={(e) => setFFrom({ code: e.target.value.toUpperCase() })} placeholder="TLV" className={inputCls} data-testid="ef-from-code" /></Field>
              <Field label="Destino (código)"><input value={f.to.code} onChange={(e) => setFTo({ code: e.target.value.toUpperCase() })} placeholder="MAD" className={inputCls} data-testid="ef-to-code" /></Field>
              <Field label="Ciudad origen"><input value={f.from.city} onChange={(e) => setFFrom({ city: e.target.value })} placeholder="Tel Aviv" className={inputCls} /></Field>
              <Field label="Ciudad destino"><input value={f.to.city} onChange={(e) => setFTo({ city: e.target.value })} placeholder="Madrid" className={inputCls} /></Field>
              <Field label="Terminal salida"><input value={f.from.terminal} onChange={(e) => setFFrom({ terminal: e.target.value })} placeholder="T3" className={inputCls} /></Field>
              <Field label="Terminal llegada"><input value={f.to.terminal} onChange={(e) => setFTo({ terminal: e.target.value })} placeholder="T4S" className={inputCls} /></Field>
            </div>
            <Field label="Salida (fecha y hora local)"><input type="datetime-local" value={f.depart || ""} onChange={(e) => setF({ depart: e.target.value })} className={inputCls} /></Field>
            <Field label="Llegada (fecha y hora local)"><input type="datetime-local" value={f.arrive || ""} onChange={(e) => setF({ arrive: e.target.value })} className={inputCls} /></Field>
            <Field label="Duración"><input value={f.duration} onChange={(e) => setF({ duration: e.target.value })} placeholder="5h 30m" className={inputCls} /></Field>
            <Field label="PNR / Localizador"><input value={f.bookingRef} onChange={(e) => setF({ bookingRef: e.target.value })} placeholder="ABC123" className={inputCls} /></Field>
            <Field label="Asientos"><input value={f.seats} onChange={(e) => setF({ seats: e.target.value })} placeholder="12A, 12B" className={inputCls} /></Field>
            <Field label="Equipaje"><input value={f.bags} onChange={(e) => setF({ bags: e.target.value })} placeholder="1 facturado + 1 mano" className={inputCls} /></Field>
            <Field label="Notas"><textarea value={f.notes} onChange={(e) => setF({ notes: e.target.value })} placeholder="Check-in 3h antes…" className={inputCls} rows={3} /></Field>
            <label className="flex items-center gap-2 mb-3 text-sm">
              <input type="checkbox" checked={!!f.pending} onChange={(e) => setF({ pending: e.target.checked })} />
              Marcar como "Por confirmar"
            </label>
          </>
        )}
      </EditSheet>

      {/* EDIT HOTEL SHEET */}
      <EditSheet
        open={!!h}
        title={h?._new ? "Añadir hotel" : "Editar hotel"}
        onClose={() => setEditingHotel(null)}
        onSave={saveHotel}
        onDelete={h?._new ? null : deleteHotel}
        saving={saving}
      >
        {h && (
          <>
            <Field label="Nombre"><input value={h.name} onChange={(e) => setH({ name: e.target.value })} placeholder="Hotel Florentin" className={inputCls} data-testid="eh-name" /></Field>
            <Field label="Ciudad"><input value={h.city} onChange={(e) => setH({ city: e.target.value })} placeholder="Tel Aviv" className={inputCls} /></Field>
            <Field label="Dirección"><input value={h.address} onChange={(e) => setH({ address: e.target.value })} placeholder="Herzl 62" className={inputCls} /></Field>
            <Field label="Barrio"><input value={h.neighborhood} onChange={(e) => setH({ neighborhood: e.target.value })} placeholder="Florentin" className={inputCls} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Check-in"><input type="datetime-local" value={h.checkIn || ""} onChange={(e) => setH({ checkIn: e.target.value })} className={inputCls} /></Field>
              <Field label="Check-out"><input type="datetime-local" value={h.checkOut || ""} onChange={(e) => setH({ checkOut: e.target.value })} className={inputCls} /></Field>
              <Field label="Noches"><input type="number" value={h.nights} onChange={(e) => setH({ nights: e.target.value })} className={inputCls} /></Field>
              <Field label="Reserva"><input value={h.bookingRef} onChange={(e) => setH({ bookingRef: e.target.value })} placeholder="ABC123" className={inputCls} /></Field>
              <Field label="PIN"><input value={h.pin} onChange={(e) => setH({ pin: e.target.value })} placeholder="1234" className={inputCls} /></Field>
              <Field label="Teléfono"><input value={h.phone} onChange={(e) => setH({ phone: e.target.value })} placeholder="+972 ..." className={inputCls} /></Field>
              <Field label="Latitud"><input type="number" step="0.000001" value={h.lat} onChange={(e) => setH({ lat: e.target.value })} placeholder="32.0588" className={inputCls} /></Field>
              <Field label="Longitud"><input type="number" step="0.000001" value={h.lng} onChange={(e) => setH({ lng: e.target.value })} placeholder="34.7707" className={inputCls} /></Field>
            </div>
            <Field label="Tip"><textarea value={h.tip} onChange={(e) => setH({ tip: e.target.value })} placeholder="A 2 min andando del mercado…" className={inputCls} rows={2} /></Field>
            <label className="flex items-center gap-2 mb-3 text-sm">
              <input type="checkbox" checked={!!h.breakfast} onChange={(e) => setH({ breakfast: e.target.checked })} />
              Incluye desayuno
            </label>
          </>
        )}
      </EditSheet>
    </div>
  );
}
